<script>
    import { onMount } from "svelte";
    import JSONTree from "svelte-json-tree";
    import { link, push } from "svelte-spa-router";
    import {
        isAuthenticated,
        auth0Client,
        hostname,
        username,
        stack,
        currentSession,
        showDebug,
        showDebugPane,
        accessInfo,
    } from "../stores.js";
    import Eye from "svelte-material-icons/Eye.svelte";
    import Download from "svelte-material-icons/Download.svelte";
    import CloudDownloadOutline from "svelte-material-icons/CloudDownloadOutline.svelte";
    import Close from "svelte-material-icons/Close.svelte";
    import FilePdfBox from "svelte-material-icons/FilePdfBox.svelte";
    import ContentCopy from "svelte-material-icons/ContentCopy.svelte";
    import {
        getScanThumbnailUrl,
        extractTomographyDatasetNameFromPath,
        isoDateToTime,
        isoDateToDate,
        millisecondsToBestTimeUnits,
        isoDurationOrSecondsToBestTimeUnits,
        isNumeric,
        now,
        getCameraDisplayNameFromString,
        getSessionDetailsApi,
        sleep,
        getReconstructionDetailsApi,
        deleteDataset,
        deleteSession,
    } from "../utils.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { toastThemeErrorDismissable, toastThemeError, toastThemeSuccessDismissable, toastThemeSuccess } from "../toasts.js";
    import { showDownloader, downloader } from "../downloadstores.js";
    import { SLICE_DOWNLOAD_TYPE_BINARY } from "../components/Downloader.svelte";
    import Header from "../components/Header.svelte";
    import { tooltip } from "svooltip";
    import "svooltip/styles.css";
    import Modal from "../components/Modal.svelte";
    import { waitingOrRunningPipelineIds } from "../pipelinestores.js";

    export let params = {}; // svelte-spa-router need sthis for route parameters
    $: $currentSession = params?.session;

    let pageName = "Details";
    let value = {
        authenticated: $isAuthenticated,
        session: params.session,
    };
    let scanDetails = [];
    let hideAdvancedDetails = true;
    let modalType = "";
    let selectedDataset = null;
    let selectedSession = null;
    let isAdmin = false;

    $: {
        if ($isAuthenticated) {
            console.log("~~~~~~~~~~~~~~~INITIALISING BECAUSE AUTHENTICATED~~~~~~~~~~~~~~~~");
            initialiseWithData();
            isAdmin = $accessInfo?.userservice_org_teams?.some((o) => o.role === "admin");
        }
    }

    //////////////// DOWNLOADING ///////////////////////
    //let downloaderComponent; // Will be initialised by creation of <Downloader>
    ///////////// END DOWNLOADING //////////////////////

    // Manipulate the data from the server before displaying
    async function showSessionDetails(details) {
        try {
            //console.log("SESSION DETAILS=", details);

            value = details;
            scanDetails = details?.tomographies || [];

            // For each tomography, find all reconstructions and add them to the scanDetails
            scanDetails.map((t) => {
                // OLD WAY: t.reconstructions = details.reconstructions?.filter( r => r.tomo_dataset_id == t.dataset_id );
                // Since EXC-788-lazy-dag-based-reconstruction-endpoint, we no longer have the fields
                // tomo_dataset_id, preview_paths, name, min_slice, max_slice, thumbnail_image_pattern. Instead, we only have
                // dataset_id, thumbnail_image.
                t.reconstructions = details.reconstructions?.filter(
                    (r) => extractTomographyDatasetNameFromPath(r.dataset_id) == t.dataset_id,
                );

                t.reconstructions?.sort((a, b) => Date.parse(b.start_time) - Date.parse(a.start_time));

                t.reconstructions?.map(async (r) => {
                    const recDetails = await getReconstructionDetailsResponse(r.dataset_id);
                    r.details = recDetails;
                    scanDetails = scanDetails;
                });

                t.geometry = calculateGeometry(t.dataset?.command?.stage_position_mm);
            });
        } catch (error) {
            console.log("PARSE DETAILS ERROR: ", error.message);
            value = {};
        }

        //console.log("SCANDETAILS: ", scanDetails);
        scanDetails = scanDetails;
    }

    function calculateGeometry(stagePositions) {
        try {
            const sod_mm = stagePositions["object_beam"];
            const sdd_mm = stagePositions["camera_beam"];
            const offset_mm = -stagePositions["camera_transverse"];
            const object_vertical_mm = stagePositions["object_vertical"];

            const R1 = sod_mm;
            const Rtot = sdd_mm;
            const R2 = Rtot - R1;
            const Mag = Rtot / R1;
            const Zeff = R2 / Mag;

            //s['effective_pixel_size_mm'] = s['pixel_size_mm']/s['Mag']
            //s['offset_mm'] = offset_mm

            return {
                R1: R1,
                Rtot: Rtot,
                R2: R2,
                Mag: Mag,
                Zeff: Zeff,
            };
        } catch (err) {
            console.log(err);
            return;
        }
    }

    function getCentershiftPreviewUrl(previewPaths) {
        // Look for a centershift thumbnail
        let previewPath;
        if (previewPaths && previewPaths.length > 0) {
            previewPath = previewPaths.find((p) => p.name.includes("centershift_preview"));
            console.log(`FOUND PREVIEW_PATH: ${JSON.stringify(previewPath)}`);
        }
        if (!previewPath) {
            return null;
        }
        return previewPath.thumbnail_path ? previewPath.thumbnail_path : previewPath.path;
    }

    function getFdkPreviewUrl(previewPaths) {
        // Find a preview slice path
        let previewPath;
        if (previewPaths && previewPaths.length > 0) {
            previewPath = previewPaths.find((p) => p.name.includes("slice"));
            console.log(`FOUND FDK PREVIEW_PATH: ${JSON.stringify(previewPath)}`);
        }
        if (!previewPath) {
            return null;
        }
        return previewPath.thumbnail_path ? previewPath.thumbnail_path : previewPath.path;
    }

    async function onViewImg(previewPath) {
        // TODO: View image
    }

    // Experimental page to use when developing refactored version of Prepare page.
    async function onOpenRecipePreview(tomography) {
        const tomo_dataset_id = tomography.dataset_id;
        const session_id = params.session;
        console.log(`session_id: ${session_id}`);
        const tomography_id = tomo_dataset_id.match(/\d+-tomo/i);
        console.log(`tomography_id: ${tomography_id}`);

        // Navigate to the Prepare view
        push(`/recipe/${session_id}/${tomography_id}/undefined`);
    }

    // Old stuff (deprecated preview page)
    async function startPreprocessing(tomography) {
        // START PREPROCESSING
        console.log("START PROCESSING FOR ", tomography);
        const tomo_dataset_id = tomography.dataset_id;
        const session_id = params.session;
        console.log(`session_id: ${session_id}`);
        const tomography_id = tomo_dataset_id.match(/\d+-tomo/i);
        console.log(`tomography_id: ${tomography_id}`);

        // Navigate to the details view
        push(`/prepare/${session_id}/${tomography_id}/undefined`);
    }

    onMount(async () => {
        // DEBUG: console.log("DOWNLOADERCOMPONENT EXPORTS: ", $downloader?.onDownloadImg);
    });

    async function initialiseWithData() {
        console.log("DETAILS INIT: isAuthenticated=", $isAuthenticated);
        if (!$username && $auth0Client && $isAuthenticated) {
            console.log("$auth0Client=", $auth0Client);
            let user = await $auth0Client.getUser();
            $username = user.nickname || user.name;
        }

        const sessionDetailsApiResponse = await getSessionDetailsApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
        );
        if (isNumeric(sessionDetailsApiResponse)) {
            let errorMsg;
            switch (sessionDetailsApiResponse) {
                case 401:
                    errorMsg = `Not authorised to read ${params.session}`;
                    break;
                case 403:
                    errorMsg = `Forbidden to read ${params.session}`;
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = sessionDetailsApiResponse;
            }
            let msg = `${now()}<br/>Session details API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);

            return; // Not logged in, don't load data
        }
        const details = sessionDetailsApiResponse;
        await showSessionDetails(details);
    }

    function closeDebugPane() {
        $showDebugPane = false;
    }

    async function getReconstructionDetailsResponse(reconstructionDatasetId) {
        const reconstructionDetailsApiResponse = await getReconstructionDetailsApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
            reconstructionDatasetId,
        );
        if (isNumeric(reconstructionDetailsApiResponse)) {
            let errorMsg;
            switch (reconstructionDetailsApiResponse) {
                case 401:
                    errorMsg = `Not authorised to read ${params.session}`;
                    break;
                case 403:
                    errorMsg = `Forbidden to read ${params.session}`;
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = reconstructionDetailsApiResponse;
            }
            let msg = `${now()}<br/>Reconstruction details API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);

            return; // Not logged in, don't load data
        }
        const details = reconstructionDetailsApiResponse;
        const name = details?.dataset_id;
        const url = details?.large_image_pattern;
        const minSlice = details?.volume?.min_slice;
        const maxSlice = details?.volume?.max_slice;
        const batchSize = 100;
        const reductionFactor = 64;

        // Download thumbnails or previews into cache. We don't expect the user to do other things while the
        // dataset loads, so use a large batch size (number of open connections)
        //$downloader?.onDownloadImg(name, url, minSlice, maxSlice, SLICE_DOWNLOAD_TYPE_PREVIEW, USE_DOWNLOAD_CACHE, batchSize, reductionFactor);

        // Get metadata
        const tomography_id = extractTomographyDatasetNameFromPath(url); // TODO: Get this from params instead?
        console.log("GETTING METADATA FOR TOMOGRAPHY ", tomography_id);
        const session_id = params.session;
        console.log(`session_id: ${session_id}`);
        console.log(`tomography_id: ${tomography_id}`);
        console.log(`dataset_id: ${name}`);

        return details;
    }

    async function onViewReconstruction(reconstructionDatasetId) {
        const reconstructionDetailsResponse = await getReconstructionDetailsResponse(reconstructionDatasetId);
        const name = reconstructionDetailsResponse?.dataset_id;
        const session_id = params.session;
        const url = reconstructionDetailsResponse?.large_image_pattern;
        const tomography_id = extractTomographyDatasetNameFromPath(url);

        // Navigate to the details view
        push(`/reconstruction/${session_id}/${tomography_id}/${name}`);
    }

    async function onDownloadReconstruction(reconstructionDatasetId, bin) {
        // Broken since EXC-788.
        // TODO: fetch dataset details for reconstruction and extract min_slice, max_slice,
        // thumbnail_image_pattern and large_image_pattern
        //$showDownloader = true;
        //$downloader?.onDownloadImg(name, url, SLICE_DOWNLOAD_TYPE_BINARY);

        const reconstructionDetailsApiResponse = await getReconstructionDetailsApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
            reconstructionDatasetId,
        );
        if (isNumeric(reconstructionDetailsApiResponse)) {
            let errorMsg;
            switch (reconstructionDetailsApiResponse) {
                case 401:
                    errorMsg = `Not authorised to read ${params.session}`;
                    break;
                case 403:
                    errorMsg = `Forbidden to read ${params.session}`;
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = reconstructionDetailsApiResponse;
            }
            let msg = `${now()}<br/>Reconstruction details API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);

            return; // Not logged in, don't load data
        }
        const details = reconstructionDetailsApiResponse;
        const name = details?.dataset_id;
        let url = details?.original_image_pattern;
        const urlBin2x = details?.original_image_pattern_binning_2;
        const urlBin4x = details?.original_image_pattern_binning_4;
        const bin1x = details?.reconstruction_bin_1x_files;
        const bin2x = details?.reconstruction_bin_2x_files;
        const bin4x = details?.reconstruction_bin_4x_files;

        if (bin == 2) url = details?.original_image_pattern_binning_2;
        if (bin == 4) url = details?.original_image_pattern_binning_4;

        const vol = getBinnedVolumeInfo(details, bin);
        const minSlice = vol?.min_slice;
        const maxSlice = vol?.max_slice;

        // Report to include
        const reportUrl = details?.report;

        $showDownloader = true;
        // Also broken in EXC-788, original_image_pattern is wrong. TODO: When fixed, use original_image_pattern.
        $downloader?.onDownloadImg(
            name,
            url,
            bin,
            minSlice,
            maxSlice,
            SLICE_DOWNLOAD_TYPE_BINARY,
            false,
            undefined,
            undefined,
            reportUrl,
        );
    }

    async function getDownloadLinkChoices(reconstructionDatasetId) {
        let scandetails = scanDetails?.find((d) =>
            d.reconstructions?.find((r) => r.dataset_id == reconstructionDatasetId),
        );
        let details = scandetails.reconstructions?.find((r) => r.dataset_id == reconstructionDatasetId)?.details;
        //DEBUG: if (details) {console.log("FOUND SCANDETAILS FOR ", reconstructionDatasetId, ": ", details);}

        const name = details?.dataset_id;
        const url = details?.original_image_pattern;
        const urlBin2x = details?.original_image_pattern_binning_2;
        const urlBin4x = details?.original_image_pattern_binning_4;
        const bin1x = details?.original_files_binning_1?.[0];
        const bin2x = details?.original_files_binning_2?.[0];
        const bin4x = details?.original_files_binning_4?.[0];

        let linkChoices = [];
        if (url)
            linkChoices.push({
                name: "no bin",
                url: url,
                filetype: getFileType(url, false),
                size: details?.size_GB * (1 - 1 / 8 - 1 / 64),
                slices: details?.volume?.max_slice - details?.volume?.min_slice + 1,
                sliceroi: `${details?.volume?.slice_roi?.right - details?.volume?.slice_roi?.left + 1} x ${details?.volume?.slice_roi?.bot - details?.volume?.slice_roi?.top + 1}`,
            });
        if (urlBin2x)
            linkChoices.push({
                name: "2x2x2",
                url: urlBin2x,
                filetype: getFileType(urlBin2x, false),
                bin: 2,
                size: getSizeGBMetricFromVolume(details, 2),
                slices: getSlicesMetricAsStringFromVolume(details, 2),
                sliceroi: getSliceRoiMetricAsStringFromVolume(details, 2),
            });
        if (urlBin4x)
            linkChoices.push({
                name: "4x4x4",
                url: urlBin4x,
                filetype: getFileType(urlBin4x, false),
                bin: 4,
                size: getSizeGBMetricFromVolume(details, 4),
                slices: getSlicesMetricAsStringFromVolume(details, 4),
                sliceroi: getSliceRoiMetricAsStringFromVolume(details, 4),
            });
        if (bin1x)
            linkChoices.push({
                name: "no bin",
                url: bin1x,
                filetype: getFileType(bin1x, true),
                size: details?.size_GB * (1 - 1 / 8 - 1 / 64),
                slices: details?.volume?.max_slice - details?.volume?.min_slice + 1,
                sliceroi: `${details?.volume?.slice_roi?.right - details?.volume?.slice_roi?.left + 1} x ${details?.volume?.slice_roi?.bot - details?.volume?.slice_roi?.top + 1}`,
            });
        if (bin2x)
            linkChoices.push({
                name: "2x2x2",
                url: bin2x,
                filetype: getFileType(bin2x, true),
                bin: 2,
                size: getSizeGBMetricFromVolume(details, 2),
                slices: getSlicesMetricAsStringFromVolume(details, 2),
                sliceroi: getSliceRoiMetricAsStringFromVolume(details, 2),
            });
        if (bin4x)
            linkChoices.push({
                name: "4x4x4",
                url: bin4x,
                filetype: getFileType(bin4x, true),
                bin: 4,
                size: getSizeGBMetricFromVolume(details, 4),
                slices: getSlicesMetricAsStringFromVolume(details, 4),
                sliceroi: getSliceRoiMetricAsStringFromVolume(details, 4),
            });

        //console.log("LINKCHOICES FOR ", reconstructionDatasetId, " :", linkChoices)
        //console.log("DETAILS: ", details);
        return linkChoices;
    }

    function getBinnedVolumeInfo(details, aBin) {
        let bin = aBin || 1;
        let vol;
        if (!bin || bin == 1) vol = details?.volume;
        else if (bin == 2) vol = details?.volume_binned_2x;
        else if (bin == 4) vol = details?.volume_binned_4x;

        return vol;
    }

    // Helper function to extract estimate of reconstructed dataset size in GB
    // Should really be close to 1/8 for bin==2 and 1/64 for bin==4, but not quite accurate
    // when number of slices is not divisible by bin. Also binned images may be slightly smaller
    function getSizeGBMetricFromVolume(details, aBin) {
        let bin = aBin || 1;
        let originalVol = getBinnedVolumeInfo(details, 1);
        let binnedVol = getBinnedVolumeInfo(details, bin);

        const originalSlices = Math.ceil(originalVol.max_slice - originalVol.min_slice + 1);
        const binnedSlices = Math.ceil((binnedVol.max_slice - binnedVol.min_slice + 1) / bin);
        const binnedOriginalSlicesFactor = binnedSlices / originalSlices;

        const originalRoi =
            Math.floor(originalVol.slice_roi?.right - originalVol.slice_roi?.left + 1) *
            Math.floor(originalVol.slice_roi?.bot - originalVol.slice_roi?.top + 1);
        const binnedRoi =
            Math.floor((binnedVol.slice_roi?.right - binnedVol.slice_roi?.left + 1) / bin) *
            Math.floor((binnedVol.slice_roi?.bot - binnedVol.slice_roi?.top + 1) / bin);
        const binnedOriginalRoiFactor = binnedRoi / originalRoi;

        // sizeGB is total size in reconstruction dataset folder, including previews, jsons and all
        // binning sizes and report document.
        // For simplicity, we ignore non-tiff data. Thus to get an estimate of the unbinned size, we
        // remove 1/8 for 2x and 1/64 for 4x.
        let sizeGB = details?.size_GB * (1 - 1 / 8 - 1 / 64) * binnedOriginalSlicesFactor * binnedOriginalRoiFactor;
        return sizeGB;
    }

    // Helper function to extract slices (number of actual slices) info from reconstruction details volume
    function getSlicesMetricAsStringFromVolume(details, aBin) {
        let bin = aBin || 1;
        let vol = getBinnedVolumeInfo(details, aBin);

        // We get the slices by taking the number of slices in the volume and dividing by the bin.
        // Example: original has slices 500..510, then 2x will have 500..509 and 4x has 500..507.
        // For correct datasets (since EXC-2168) the slice index + 1 should always align with a bin modulo,
        // but for older datasets, the slice index does not align. Since we know that the earlier
        // implementation sometimes gave these datasets an extra (incomplete) binned slice, we round up.
        const slices = Math.ceil((vol.max_slice - vol.min_slice + 1) / bin);

        return `${slices}`;
    }

    // Helper function to extract slice roi (actual pixels dimensions) info from reconstruction details volume
    function getSliceRoiMetricAsStringFromVolume(details, aBin) {
        let bin = aBin || 1;
        let vol = getBinnedVolumeInfo(details, aBin);

        const sliceroi = `${Math.floor((vol.slice_roi?.right - vol.slice_roi?.left + 1) / bin)} x ${Math.floor((vol.slice_roi?.bot - vol.slice_roi?.top + 1) / bin)}`;

        return sliceroi;
    }

    function getFileType(url, isSingleFileArchive) {
        if (!url) return "invalid ";
        if (!isSingleFileArchive) {
            if (url.includes(".tif")) return "TIFF slices";
        }
        if (url.includes(".tif")) return "BigTIFF";
        if (url.includes(".btf")) return "BigTIFF";
        if (url.includes(".zip")) return "ZIP";
        return;
    }

    // Helper function to convert to a human-readable memory size
    // Less than 1 GB will return MB or even kB
    // NOTE: We are showing GB, but the datapipeline gives us IEC sizes, so should
    // probably show KiB, MiB and GiB.
    function formatMemorySize(sizeGB) {
        if (sizeGB < 1 / 1024) return `${Math.round(sizeGB * 1024 * 1024)} KB`;
        if (sizeGB < 1) return `${Math.round(sizeGB * 1024)} MB`;
        if (sizeGB >= 1) return `${Math.round(sizeGB)} GB`;
    }

    async function confirmDeleteDataset() {
        modalType = "";
        const response = await deleteDataset($hostname, $stack, $currentSession, selectedDataset.dataset_id);
        if (isNumeric(response)) {
            toast.push(
                `Server problem while deleting data ${selectedDataset.dataset_id}`,
                toastThemeSuccessDismissable,
            );
            const errMsg = `Delete dataset error for ${selectedDataset?.dataset_id}: ${response}`;
            selectedDataset = null;
            throw Error(errMsg); // Log for Sentry
        } else {
            toast.push(`Successfully deleted ${selectedDataset.dataset_id}`, toastThemeErrorDismissable);
            initialiseWithData();
        }
        selectedDataset = null;
    }

    function cancelDataset() {
        modalType = "";
        selectedDataset = null;
    }

    async function confirmArchiveDataset() {
        console.log("archive");
    }

    async function confirmDeleteSession(session) {
        modalType = "";
        const response = await deleteSession($hostname, $stack, session);
        if (isNumeric(response)) {
            let msg;
            if (response >= 500 && response < 600) {
                msg = `Server error while deleting session ${session}`;
            } else if (response == 401) {
                msg = `Not authorised to delete session ${session}`;
            } else if (response == 404) {
                msg = `Delete session functionality not available. Session ${session} not deleted`;
            }
            toast.push(msg, toastThemeErrorDismissable);
        } else {
            toast.push(`Successfully requested deletion of ${session}`, toastThemeSuccessDismissable);
            initialiseWithData();
        }
        selectedSession = null;
    }

    function cancelSession() {
        modalType = "";
        selectedSession = null;
    }

    function onClickDeleteData(dataset) {
        modalType = "delete";
        selectedDataset = dataset;
    }

    function onClickArchiveData(dataset) {
        modalType = "archive";
        selectedDataset = dataset;
    }

    function onClickDeleteSession() {
        modalType = "deleteSession";
        selectedSession = $currentSession;
    }

    function onClickDeleteRunningSession() {
        modalType = "deleteRunningSession";
        selectedSession = $currentSession;
    }

    let azCommand;
    function onShowAzCopyCommand(recDataset, bin) {
        azCommand = undefined;
        modalType = "showAzCopyCommand";
        selectedDataset = recDataset.dataset_id;

        // If the user is an admin, we can use the download_dir_pattern and thereby
        // allow download of all files in a directory.
        // If the user is not admin, we only allow download of a single file, e.g. zip.
        let urlPattern;
        if (isAdmin) {
            urlPattern = recDataset.details.download_dir_pattern;
        }
        let excludePattern = undefined;
        let isBinning1PatternUsed = recDataset.details.original_image_pattern?.includes('binning_1');
        let isBinning1FileUsed = recDataset.details.original_files_binning_1?.[0].includes('binning_1');
        let isBinning2FileUsed = recDataset.details.original_files_binning_2?.[0].includes('binning_2');
        let isBinning4FileUsed = recDataset.details.original_files_binning_4?.[0].includes('binning_4');
        let includePattern = undefined;
        if (bin == 1 || isBinning1PatternUsed || isBinning1FileUsed) {
            includePattern = `*binning_1*`;
            if (isBinning1FileUsed) urlPattern = recDataset.details.original_files_binning_1[0];
        } if (bin == 2) {
            includePattern = `*binning_2*`;
            if (isBinning2FileUsed) urlPattern = recDataset.details.original_files_binning_2[0];
        } else if (bin == 4) {
            includePattern = `*binning_4*`;
            if (isBinning4FileUsed) urlPattern = recDataset.details.original_files_binning_4[0];
        }
        let prepareEnv = ""; //"export AZCOPY_CRED_TYPE=Anonymous; export AZCOPY_CONCURRENCY_VALUE=AUTO;"
        let resetEnv = ""; //"unset AZCOPY_CRED_TYPE; unset AZCOPY_CONCURRENCY_VALUE;"
        let downloadsRootFolder = "."; // TODO: Set to something sane for Mac/Windows/Linux
        let destFolder = `${downloadsRootFolder}/${params.session}/${recDataset.dataset_id}`;
        let dest = `${destFolder}`;
        const regexp = /\/Binaries\/(\w+\.\w+)[/?]*/;
        const matched = urlPattern.match(regexp);
        const matchedFilename = matched?.[1];
        if (matchedFilename) {
            dest = `${destFolder}/Binaries/${matchedFilename}`;
        }
        let flags = '';
        if (isAdmin && includePattern && !isBinning1FileUsed) {
            flags = `--include-pattern="${includePattern};report.pdf" `;
        }
        if (isAdmin && excludePattern && !isBinning1FileUsed) {
            flags += `--exclude-path="${excludePattern}" `;
        }
        flags += `--overwrite=true --check-md5=FailIfDifferent --from-to=BlobLocal --recursive=true --log-level=INFO`;
        let sasUrl = urlPattern;

        //azCommand = `${prepareEnv} azcopy copy "${sasUrl}" ${dest} ${flags}; ${resetEnv}`;
        azCommand = `azcopy copy ${flags} "${sasUrl}" ${dest}`;
    }

    // Function to copy text to clipboard
    async function copyToClipboard() {
        try {
            await navigator.clipboard.writeText(azCommand);
            let msg = `${now()}<br/>Command copied to clipboard`;
            toast.push(msg, toastThemeSuccess);
        } catch (err) {
            console.error("Failed to copy: ", err);
            let msg = `${now()}<br/>Failed to copy the command`;
            toast.push(msg, toastThemeErrorDismissable);
        }
    }
</script>

<Header />

<main-window>
    <nav><a href="/" use:link>Home</a></nav>
    <div>{params.session}&nbsp;({$isAuthenticated ? "Authenticated" : "Not authenticated"})</div>
    <detail-pane>
        <h2 style="margin-bottom: 0.2em;">Details</h2>
        {#if value?.name}
            <div>{value?.name}</div>
        {/if}
        {#if $showDebug}
            <div style="--json-tree-font-size: 16px;font-weight: bold;">
                <JSONTree {value} />
            </div>
        {/if}
    </detail-pane>
    {#each scanDetails as d}
        <detail-box>
            <div>
                <h3>
                    Scan &emsp; <span style="font-weight: normal; font-size: 16px;">{d.dataset_id}</span>
                </h3>
                <p style="white-space: pre-wrap">{d.description}</p>
                <image-box style="background-image: url({getScanThumbnailUrl(d)})" />
                <span style="display: inline-block; vertical-align: top;">
                    <table>
                        <thead>
                            <th>Source voltage</th>
                            <th>Effective pixel size</th>
                            <th>Field of view</th>
                            <th>Region of interest</th>
                            <th>Camera</th>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{d.source_voltage_V / 1000} kV</td>
                                <td>{d.pixel_size_um?.toFixed(2)} μm</td>
                                <td>
                                    {d.field_of_view?.horizontal_mm?.toFixed(2)} mm x {d.field_of_view?.vertical_mm?.toFixed(
                                        2,
                                    )} mm
                                </td>
                                <td>
                                    {d?.dataset?.command?.camera?.roi_px?.bot -
                                        d?.dataset?.command?.camera?.roi_px?.top +
                                        1} x {d?.dataset?.command?.camera?.roi_px?.right -
                                        d?.dataset?.command?.camera?.roi_px?.left +
                                        1}
                                </td>
                                <td>{getCameraDisplayNameFromString(d?.dataset?.command?.camera?.name)}</td>
                            </tr>
                        </tbody>
                    </table>
                    <br />
                    <table>
                        <thead>
                            <th>Start time</th>
                            <th>User</th>
                            <th>Scan duration</th>
                            <th>Exposure duration</th>
                            <th>Effective propagation distance</th>
                            <th>Number of images</th>
                            <th>Disk size</th>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{isoDateToDate(d.start_time)}&nbsp;{isoDateToTime(d.start_time)}</td>
                                <td>
                                    {d?.dataset?.scan_information?.user
                                        ? d?.dataset?.scan_information?.user
                                        : value?.user}
                                </td>
                                <td>{isoDurationOrSecondsToBestTimeUnits(d.scan_time)}</td>
                                <td>
                                    {millisecondsToBestTimeUnits(
                                        d?.dataset?.command?.acquisition?.number_images *
                                            d?.dataset?.command?.camera?.exposure_s *
                                            1000 *
                                            d?.dataset?.command?.camera?.averages,
                                    )}
                                </td>
                                <td>{d?.propagation_distance_mm?.toFixed(2)} mm</td>
                                <td>
                                    {d?.dataset?.command?.acquisition?.number_images}
                                </td>
                                <td>{d?.size_GB?.toFixed(0)} GB</td>
                            </tr>
                        </tbody>
                    </table>
                    <br />
                    {#if hideAdvancedDetails}
                        <table>
                            <tfoot style="text-align: center; border: none;">
                                <button
                                    class="tertiary"
                                    on:click={() => {
                                        hideAdvancedDetails = false;
                                    }}>
                                    Show more
                                </button>
                            </tfoot>
                        </table>
                    {:else}
                        <table>
                            <thead>
                                <tr>
                                    <th>Localised</th>
                                    <th>Offset</th>
                                    <th>Multirevolution</th>
                                    <th>HW ring correction</th>
                                    <th>Step size</th>
                                </tr>
                            </thead>
                            <tbody>
                                <td>
                                    {d?.dataset?.default_parameters?.reconstruction?.is_localized ? "true" : "false"}
                                </td>
                                <td>
                                    {d?.dataset?.default_parameters?.reconstruction?.is_offset_scan ? "true" : "false"}
                                </td>
                                <td>
                                    {d?.dataset?.command?.multirevolution?.enabled
                                        ? d?.dataset?.command?.multirevolution?.number_revolutions
                                        : "false"}
                                </td>
                                <td>{d?.dataset?.command?.ring_correction?.enabled ? "true" : "false"}</td>
                                <td>{d?.dataset?.command?.acquisition?.step_size_deg?.toPrecision(2)} °</td>
                            </tbody>
                        </table>
                        <h4 style="line-height: 1.5em; margin-block-start: 8px; margin-block-end: 4px;">Source</h4>
                        <table>
                            <thead>
                                <tr>
                                    <th>Spot center</th>
                                    <th>Spot height</th>
                                    <th>Spot width</th>
                                    <th>Power</th>
                                    <th>Filter material</th>
                                    <th>Filter thickness</th>
                                </tr>
                            </thead>
                            <tbody>
                                <td>{d?.dataset?.command?.source?.["e-beam_um"]?.center} μm</td>
                                <td>{d?.dataset?.command?.source?.["e-beam_um"]?.height?.toFixed(1)} μm</td>
                                <td>{d?.dataset?.command?.source?.["e-beam_um"]?.width} μm</td>
                                <td>{d?.dataset?.command?.source?.power_W?.toFixed(0)} W</td>
                                <td>{d?.dataset?.command?.filter?.material}</td>
                                <td>{d?.dataset?.command?.filter?.thickness_mm} mm</td>
                            </tbody>
                        </table>
                        <h4 style="line-height: 1.5em; margin-block-start: 8px; margin-block-end: 4px;">Camera</h4>
                        <table>
                            <thead>
                                <tr>
                                    <th>Gain</th>
                                    <th>Binning</th>
                                    <th>Exposure time</th>
                                    <th>Averages</th>
                                    <th>Pixel size (after binning)</th>
                                    <th>Region of interest</th>
                                </tr>
                            </thead>
                            <tbody>
                                <td>{d?.dataset?.command?.camera?.gain}</td>
                                <td>
                                    {d?.dataset?.command?.camera?.binning
                                        ? `${d?.dataset?.command?.camera?.binning?.horizontal}x${d?.dataset?.command?.camera?.binning?.vertical}`
                                        : "false"}
                                </td>
                                <td>{d?.dataset?.command?.camera?.exposure_s} s</td>
                                <td>{d?.dataset?.command?.camera?.averages}</td>
                                <td>{(d?.pixel_size_um * d?.geometry?.Mag).toFixed(2)} μm</td>
                                <td>
                                    ({d?.dataset?.command?.camera?.roi_px?.left}, {d?.dataset?.command?.camera?.roi_px
                                        ?.top}) - ({d?.dataset?.command?.camera?.roi_px?.bot}, {d?.dataset?.command
                                        ?.camera?.roi_px?.right})
                                </td>
                            </tbody>
                        </table>
                        <h4 style="line-height: 1.5em; margin-block-start: 8px; margin-block-end: 4px;">
                            Geometry &nbsp;<!--annotation style="font-weight:lighter;">(rounded)</annotation-->
                        </h4>
                        <table>
                            <thead>
                                <tr>
                                    <th>Source object distance (R1)</th>
                                    <th>Source detector distance (Rtot)</th>
                                    <th>Object detector distance (R2)</th>
                                    <th>Magnification</th>
                                </tr>
                            </thead>
                            <tbody>
                                <td>{d?.geometry?.R1?.toPrecision(3)} mm</td>
                                <td>{d?.geometry?.Rtot?.toFixed(0)} mm</td>
                                <td>{d?.geometry?.R2?.toFixed(0)} mm</td>
                                <td>{d?.geometry?.Mag?.toPrecision(2)}</td>
                            </tbody>
                        </table>
                        <br />
                        <table>
                            <thead>
                                <tr>
                                    <th>Camera transverse</th>
                                    <th>Initial angle (r)</th>
                                    <th>Object vertical (v)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <td>{d?.dataset?.command?.stage_position_mm?.camera_transverse?.toPrecision(3)} mm</td>
                                <td>{d?.dataset?.command?.stage_position_mm?.object_rotation?.toFixed(1)} °</td>
                                <td>{d?.dataset?.command?.stage_position_mm?.object_vertical?.toFixed(1)} mm</td>
                            </tbody>
                        </table>
                        <br />
                        <table>
                            <tfoot style="text-align: center; border: none;">
                                <button
                                    class="tertiary"
                                    on:click={() => {
                                        hideAdvancedDetails = true;
                                    }}>
                                    Show less
                                </button>
                            </tfoot>
                        </table>
                    {/if}
                </span>
                <br />
                {#if d.availability === "AVAILABLE"}
                    <button class="button" on:click={onOpenRecipePreview(d)}>Preview & create reconstruction</button>
                    {#if $accessInfo?.userservice_org_teams?.some((o) => o.role == "admin")}
                        <button class="error" on:click={onClickDeleteData(d)}>Delete raw data</button>
                    {/if}
                {:else}
                    <h3 style="color: red;">TOMOGRAPHY DELETED</h3>
                {/if}
                <br />
                <h4>Reconstructions</h4>
                <table class="reconstructions-table">
                    <thead>
                        <th>Started</th>
                        <th>Status</th>
                        <th>Preview</th>
                        <th>Settings</th>
                        <th>View</th>
                        <th>Download</th>
                        <th use:tooltip={{ content: `Approximate download size`, placement: "top", delay: [1000, 0] }}>
                            Size
                        </th>
                        <th>Voxel size</th>
                        <th># slices</th>
                        <th>Slice ROI</th>
                        {#if $accessInfo?.userservice_org_teams?.some((o) => o.role === "admin")}
                            <th>Storage</th>
                        {/if}
                    </thead>
                    {#each d.reconstructions as r}
                        <tbody>
                            <!-- svelte-ignore a11y-missing-attribute -->
                            <tr>
                                <td style="width: 10%">{isoDateToDate(r.start_time)} {isoDateToTime(r.start_time)}</td>
                                <td>{r.status}</td>
                                <td><preview><img src={r.thumbnail_image} /></preview></td>
                                <td>
                                    Centershift: {isNumeric(r.details?.reconstruction_settings?.centershift_object_mm)
                                        ? (r.details?.reconstruction_settings?.centershift_object_mm * 1000).toFixed(3)
                                        : ""} μm
                                    <br />
                                    {#if $showDebug}
                                        Tilt: {isNumeric(r.details?.reconstruction_settings?.tilt_deg)
                                            ? r.details?.reconstruction_settings?.tilt_deg?.toPrecision(3)
                                            : 0} °
                                        <br />
                                    {/if}
                                    Beam hardening: {isNumeric(r.details?.reconstruction_settings?.beamhard_cupping)
                                        ? r.details?.reconstruction_settings?.beamhard_cupping
                                        : 0}
                                    <br />
                                    δ: {isNumeric(r.details?.phase_retrieval_settings?.phase_retrieval?.delta)
                                        ? r.details?.phase_retrieval_settings?.phase_retrieval?.delta?.toExponential()
                                        : ""}
                                    <br />
                                    μ: {isNumeric(r.details?.phase_retrieval_settings?.phase_retrieval?.mu)
                                        ? r.details?.phase_retrieval_settings?.phase_retrieval?.mu?.toPrecision(3)
                                        : ""}
                                    <br />
                                    Slices: {r?.volume?.min_slice}..{r?.volume?.max_slice}
                                </td>
                                <td class="tv">
                                    <div>
                                        <Eye color="currentColor" size="1.5em" viewBox="0 2 24 24" />&nbsp;
                                        <a
                                            href="."
                                            on:click|preventDefault={() => onViewReconstruction(r.dataset_id)}
                                            use:tooltip={{
                                                content: `View reconstructed slices of ${r.dataset_id} in browser`,
                                                placement: "top",
                                                delay: [1000, 0],
                                            }}>
                                            View
                                        </a>
                                    </div>
                                    {#if r.details?.report}
                                        <div>
                                            <FilePdfBox color="currentColor" size="1.5em" viewBox="0 2 24 24" />&nbsp;
                                            <a 
                                                href={r.details?.report} target="_blank"
                                                use:tooltip={{
                                                    content: `${r.dataset_id}`,
                                                    placement: "top",
                                                    delay: [1000, 1000],
                                            }}>Report</a>
                                        </div>
                                    {/if}
                                </td>
                                <td class="download-cell">
                                    <table class="download-links">
                                        {#await getDownloadLinkChoices(r.dataset_id)}
                                            getting links...
                                        {:then downloadLinkChoices}
                                            {#each downloadLinkChoices as linkChoice, index}
                                                <tr>
                                                    <td>
                                                        <div class="download-link-icon">
                                                            <Download color="currentColor" size="1.5em" />
                                                        </div>
                                                        {#if linkChoice?.filetype == "TIFF slices"}
                                                            {#if linkChoice?.name == "no bin"}
                                                                <a
                                                                    href="."
                                                                    on:click|preventDefault={() =>
                                                                        onDownloadReconstruction(
                                                                            r.dataset_id,
                                                                            linkChoice?.bin,
                                                                        )}>
                                                                    Download full reconstruction ({linkChoice?.filetype})
                                                                </a>
                                                            {:else}
                                                                <a
                                                                    href="."
                                                                    on:click|preventDefault={() =>
                                                                        onDownloadReconstruction(
                                                                            r.dataset_id,
                                                                            linkChoice?.bin,
                                                                        )}>
                                                                    Download {linkChoice?.name} binned data
                                                                </a>
                                                                &nbsp;
                                                            {/if}
                                                        {:else if linkChoice?.filetype != "TIFF slices" && linkChoice?.name == "no bin"}
                                                            <a href={linkChoice?.url}>
                                                                Download full reconstruction ({linkChoice?.filetype})
                                                            </a>
                                                            &nbsp;
                                                        {:else}
                                                            <a href={linkChoice?.url}>
                                                                Download {linkChoice?.name} binned data
                                                            </a>
                                                            &nbsp;
                                                        {/if}
                                                        {#if ($accessInfo?.userservice_org_teams?.some((o) => o.role === "admin")
                                                                || (value?.user_id == $accessInfo?.userservice_userid && linkChoice?.filetype != "TIFF slices") )}
                                                            <br />
                                                            <div class="download-link-icon">
                                                                <CloudDownloadOutline
                                                                    color="currentColor"
                                                                    size="1.5em" />
                                                            </div>
                                                            <a
                                                                href="."
                                                                on:click|stopPropagation|preventDefault={() =>
                                                                    onShowAzCopyCommand(r, linkChoice?.bin)}>
                                                                Show copy command
                                                            </a>
                                                        {/if}
                                                    </td>
                                                </tr>
                                            {/each}
                                        {/await}
                                    </table>
                                </td>
                                <td class="download-size">
                                    <table class="download-links">
                                        {#await getDownloadLinkChoices(r.dataset_id)}
                                            {r.size_GB ? Math.round(r.size_GB) + " GB" : ""}
                                        {:then downloadLinkChoices}
                                            {#each downloadLinkChoices as linkChoice}
                                                <tr>
                                                    <td>
                                                        {linkChoice.size ? formatMemorySize(linkChoice.size) : ""}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            {/each}
                                        {/await}
                                    </table>
                                </td>
                                <td class="download-voxelsize">
                                    <table class="download-links">
                                        {#await getDownloadLinkChoices(r.dataset_id)}
                                            {d.pixel_size_um?.toFixed(2)} μm
                                        {:then downloadLinkChoices}
                                            {#each downloadLinkChoices as linkChoice}
                                                <tr>
                                                    <td>
                                                        {d.pixel_size_um?.toFixed(2) *
                                                            (linkChoice.bin ? linkChoice.bin : 1)} μm
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            {/each}
                                        {/await}
                                    </table>
                                </td>
                                <td class="download-slices">
                                    <table class="download-links">
                                        {#await getDownloadLinkChoices(r.dataset_id)}
                                            {r.max_slice - r.min_slice + 1}
                                        {:then downloadLinkChoices}
                                            {#each downloadLinkChoices as linkChoice}
                                                <tr>
                                                    <td>
                                                        {linkChoice.slices}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            {/each}
                                        {/await}
                                    </table>
                                </td>
                                <td class="download-sliceroi">
                                    <table class="download-links">
                                        {#await getDownloadLinkChoices(r.dataset_id)}
                                            {r?.volume?.slice_roi?.right - r?.volume?.slice_roi?.left + 1} x {r?.volume
                                                ?.slice_roi?.bot -
                                                r?.volume?.slice_roi?.top +
                                                1}
                                        {:then downloadLinkChoices}
                                            {#each downloadLinkChoices as linkChoice}
                                                <tr>
                                                    <td>
                                                        {linkChoice.sliceroi}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            {/each}
                                        {/await}
                                    </table>
                                </td>
                                {#if $accessInfo?.userservice_org_teams?.some((o) => o.role === "admin")}
                                    <td class="download-delete">
                                        <div style="display: grid;">
                                            <button class="error" on:click={onClickDeleteData(r)}>Delete</button>
                                            {#if false}<button on:click={onClickArchiveData(r)}>Archive</button>{/if}
                                        </div>
                                    </td>
                                {/if}
                            </tr>
                        </tbody>
                    {/each}
                    <br />
                </table>
            </div>
        </detail-box>
    {/each}
    <br />
    <annotation>NOTE! All times in UTC!</annotation>
    <br />
    {#if modalType === "delete"}
        <Modal on:ok={() => confirmDeleteDataset(selectedDataset)} on:close={cancelDataset}>
            <h1>Confirm deletion</h1>
            {#if !selectedDataset.dataset_id?.includes("rec")}<p>
                    If the tomography data is removed, you won't be able to generate new reconstructions on tomography {selectedDataset.dataset_id}
                </p>
            {:else}
                <p>Reconstructed dataset {selectedDataset.dataset_id} will be deleted and cannot be undone.</p>
            {/if}
            <image-box style="background-image: url({selectedDataset.thumbnail_image})" />
            <br />
            <p>
                <b>Created:</b>
                {isoDateToDate(selectedDataset.start_time)}&nbsp;{isoDateToTime(selectedDataset.start_time)}
            </p>
            <p>
                <b>Size:</b>
                {formatMemorySize(selectedDataset.size_GB)}
            </p>
        </Modal>
    {/if}
    {#if modalType === "archive"}
        <Modal on:ok={() => confirmArchiveDataset(selectedDataset)} on:close={cancelDataset}>
            <h1>Confirm archive</h1>
            {#if !selectedDataset.dataset_id?.includes("rec")}<p>
                    If the tomography data is archived, you will have to wait a longer time if you want to generate a
                    new reconstructions on this tomography {selectedDataset.dataset_id}
                </p>
            {:else}
                <p>Reconstructed dataset {selectedDataset.dataset_id} will be archived</p>
            {/if}
            <image-box style="background-image: url({selectedDataset.thumbnail_image})" />
            <br />
            <p>
                <b>Created:</b>
                {selectedDataset.start_time}
            </p>
            <p>
                <b>Size:</b>
                {formatMemorySize(selectedDataset.size_GB)}
            </p>
        </Modal>
    {/if}
    {#if modalType === "deleteSession"}
        <Modal on:ok={() => confirmDeleteSession($currentSession)} on:close={cancelSession}>
            <h1>Confirm deletion of entire session</h1>
            <p>
                All raw scan data and reconstructions for session &quot;{value?.name}&quot; ({selectedSession}) will be
                deleted and cannot be undone.
            </p>
            <p>{value?.tomographies?.length} scans and {value?.reconstructions?.length} reconstructions</p>
            <p>
                <b>Created:</b>
                {isoDateToDate(value?.start_time)}&nbsp;{isoDateToTime(value?.start_time)}
            </p>
            <p>
                <b>Size:</b>
                {formatMemorySize(value?.size_GB)}
            </p>
            <annotation style="margin-left: 0">
                Please note that it may take a few minutes before the reclaimed capacity is reflected in the storage
                usage report
            </annotation>
        </Modal>
    {/if}
    {#if modalType === "deleteRunningSession"}
        <Modal on:close={cancelSession} okable={false}>
            <h1>Cannot delete entire session</h1>
            <p>
                There is at least one Running or Waiting pipeline in session &quot;{value?.name}&quot; ({selectedSession}).
            </p>
            <p>Please make sure those pipelines are Done or Failed and then try again.</p>
            <p>
                Pipelines:
                {#each $waitingOrRunningPipelineIds as p}
                    {#if p.indexOf($currentSession) > -1}
                        <ul>{p.replace("sessions/", "")}</ul>
                    {/if}
                {/each}
            </p>
        </Modal>
    {/if}
    {#if modalType === "showAzCopyCommand"}
        <Modal on:close={cancelSession} okable={false}>
            <h1>Copy command (external tool)</h1>
            <p>Paste this command in your terminal. It requires that you have azcopy <a href="https://learn.microsoft.com/en-us/azure/storage/common/storage-use-azcopy-v10?tabs=dnf" target="_blank">installed</a>.</p>
            <p>The command is valid for 8 hours. Do not share with unauthorised personnel.</p>
            <div class="code-block">
                {azCommand}
            </div>
            <div class="az-command-buttons">
                <button on:click={copyToClipboard}>
                    <ContentCopy color="currentColor" size="1.5em" />Copy
                </button>
            </div>
        </Modal>
    {/if}
    {#if $accessInfo?.userservice_org_teams?.some((o) => o.role == "admin")}
        <div style="margin: 8px;">
            {#if $waitingOrRunningPipelineIds?.some((p) => p.indexOf($currentSession) > -1)}
                <!-- Don't enable this button if there is a pipeline in Waiting or Running state -->
                <button
                    type="button"
                    class="disabled"
                    on:click|stopPropagation|preventDefault={onClickDeleteRunningSession}>
                    Delete entire session
                </button>
            {:else}
                <button type="button" class="error" on:click|stopPropagation|preventDefault={onClickDeleteSession}>
                    Delete entire session
                </button>
            {/if}
        </div>
    {/if}
</main-window>

{#if $showDebug && $showDebugPane}
    <debug-pane>
        <close-x-button on:click={closeDebugPane}>
            <Close color="currentColor" size="1.5em" />
        </close-x-button>
        About:
        <a href="/about/321" use:link>About</a>
        <br />
        User: {$username}
        <br />
        Authenticated: {$isAuthenticated}
        <br />
        Stack: {$stack?.name}
        <br />
    </debug-pane>
{/if}

<style>
    /*:global(body) {
        padding: 0;
        margin: 0 auto;
    }*/

    main-window {
        text-align: left;
        padding: 0;
        margin: 0 auto;
        display: block;
        height: calc(100% - 224px);
        height: -moz-calc(100% - 224px);
        height: -webkit-calc(100% - 224px);
    }

    @media (min-width: 640px) {
        main-window {
            max-width: none;
        }
    }

    detail-pane {
        height: 100%;
        display: inline-block;
        padding: 0 8px 2px 8px;
        background-color: var(--var-theme-base-background-color);
        vertical-align: top;
        overflow-x: hidden;
        overflow-y: scroll;
    }

    detail-pane {
        width: 100%;
    }

    detail-box > div {
        border-bottom: 1px solid var(--var-theme-secondary-color);
        /* border: none; */
        background-color: transparent;
        padding: 8px;
        margin: 8px;
    }

    .az-command-buttons {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        padding-top: 4px;
    }
    .az-command-buttons button {
        display: flex;
        align-items: center;
        padding-bottom: 8px; /* Allow for 1.5em icon */
        padding-top: 8px; /* Allow for 1.5em icon */
    }

    .reconstructions-table {
        min-width: none;
    }

    .tv {
        min-width: 4em;
    }

    .tv div {
        padding: 8px;
        margin: 8px;
    }

    .download-cell {
        padding: 0;
        width: 10%;
    }
    .download-links {
        flex-direction: column;
        padding: 0;
        margin: 0;
        width: 100%;
        border: 0;
        line-height: 1.5em;
        height: 100%;
    }

    .download-links tr {
        border: 0;
    }

    .download-links td {
        border-top: 1px solid grey;
        border-left: 0;
        border-right: 0;
        border-collapse: collapse;
        vertical-align: middle;
    }

    .download-links tr:last-child td,
    .download-links tr:first-child td {
        border: 0;
    }

    .download-link-icon {
        display: inline-block;
        vertical-align: bottom;
        line-height: 1em;
        margin-bottom: -0.2em;
    }

    .download-size {
        padding: 0;
        width: 5%;
        height: 0;
    }

    .download-slices {
        padding: 0;
        width: 5%;
        height: 0;
    }

    .download-voxelsize {
        padding: 0;
        width: 5%;
        height: 0;
    }

    .download-sliceroi {
        padding: 0;
        width: 5%;
        height: 0;
    }

    .download-delete {
        width: 2em;
    }

    .code-block {
        background: var(--var-theme-background-color);
        color: lightgreen;
        padding: 1em;
        font-family: "Courier New", Courier, monospace;
        font-size: 1.1em;
        overflow-wrap: anywhere;
    }

    preview img {
        height: 64px;
        max-width: 100%;
    }

    image-box {
        background-color: black;
        background-size: contain;
        width: 154px;
        height: 154px;
        display: inline-block;
        background-repeat: no-repeat;
        background-position: center;
    }

    debug-pane {
        display: block;
        padding: 8px;
        background-color: #fafafaf0;
        font-size: small;
        text-align: left;
        overflow-wrap: break-word;
        width: 100%;
        position: fixed;
        bottom: 0;
    }
    close-x-button {
        position: absolute;
        top: 0;
        right: 32px;
        background-color: transparent;
        color: #333;
    }
    close-x-button:hover {
        cursor: pointer;
    }
</style>
